/// <amd-module name="Build/Designer/YamlResourcesService" />
import { BuildDefinition, BuildDefinitionVariable, BuildProcessResources } from "Build/Client/Contracts/Build";

import { IObservableArray, IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { IVssObservableService, Services, VssObservableService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface IYamlResourcesService extends IVssObservableService<NamedBuildDefinitionVariable[]> {
    getVariables(): NamedBuildDefinitionVariable[];
    getAvailableQueues(): IReadonlyObservableArray<ResourceReference<number>>;
    updateVariables(variables: NamedBuildDefinitionVariable[]): void;
    isVariablesChanged(): boolean;
}

export interface ResourceReference<TKey> {
    id: TKey;
    name: string;
}

export interface NamedBuildDefinitionVariable extends BuildDefinitionVariable {
    name: string;
}

interface PipelineResourcesData {
    queues: ResourceReference<number>[];
}

interface PipelineEditorData {
    definition: BuildDefinition;
    content: string;
    objectId: string;
}

/**
 * A collection of all resources (endpoints, queues, files, variable groups) available in the system
 * These may or may not be authorized for use on the pipeline
 */
export class AvailableResourcesModel {
    private _queues: ObservableArray<ResourceReference<number>>;

    constructor(availableResources: BuildProcessResources) {
        const queues = availableResources && availableResources.queues || [];
        this._queues = new ObservableArray<ResourceReference<number>>(queues.map((q) => {
            return {
                id: q.id,
                name: q.alias
            };
        }));
    }

    public get queues(): IObservableArray<ResourceReference<number>> {
        return this._queues;
    }
}

const enum  YamlResourcesActions {
    updateVariables = "updateVariables",
}

class YamlResourcesService extends VssObservableService<NamedBuildDefinitionVariable[]> implements IYamlResourcesService {
    private static ContributionKey: string = "ms.vss-build-web.pipeline-resources-data-provider";
    private static PipelineDataProviderKey = "ms.vss-build-web.pipeline-editor-data-provider";
    private _variables: NamedBuildDefinitionVariable[];
    private _originalJson: string;

    public getVariables(): NamedBuildDefinitionVariable[] {
        if (!this._variables) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const pipelineEditorData =  contributionService.getData<PipelineEditorData>(YamlResourcesService.PipelineDataProviderKey);
            const variables =  (pipelineEditorData && pipelineEditorData.definition && pipelineEditorData.definition.variables) || {};

            this._variables = Object.keys(variables)
                .map(key => ({
                    name: key,
                    ...variables[key]
                } as NamedBuildDefinitionVariable))
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase(), navigator.language));

            this._originalJson = JSON.stringify(this._variables);
        }

        // always return a shallow copy of the array to prevent the array order from being modified outside of this service
        return [...this._variables];
    }

    public isVariablesChanged(): boolean {
         // check to see if the new variables are differnt from original
         return (JSON.stringify(this._variables.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase(), navigator.language))) !== this._originalJson);
    }

    public getAvailableQueues(): IReadonlyObservableArray<ResourceReference<number>> {
        const queues: ObservableArray<ResourceReference<number>> = new ObservableArray<ResourceReference<number>>();
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const resourcesData = contributionService.getData<PipelineResourcesData>(YamlResourcesService.ContributionKey);
        if (resourcesData) {
            queues.value = resourcesData.queues;
        }

        return  queues;
    }

    public updateVariables(variables: NamedBuildDefinitionVariable[]) {
        this._variables = variables;
        this._notify([...this._variables], YamlResourcesActions.updateVariables.toString());
    }

    public getBuildDefinitionVariables(): { [key: string] : BuildDefinitionVariable; } {
        return this._variables.reduce(
            (map, variable) => {
                map[variable.name] = {...variable} as BuildDefinitionVariable;
                return map;
            },
            {} as { [key: string] : BuildDefinitionVariable;}
        );
    }
}

Services.add("IYamlResourcesService", { serviceFactory: YamlResourcesService });
